import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Phone, CheckCircle, Schedule, Payment, TrendingUp } from '@mui/icons-material';
import apiService from '../services/apiService';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { useNavigate } from 'react-router-dom';
import SearchableSelect from './dashboard/components/SearchableSelect';
import DrillDownModal from '../components/DrillDownModal';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

const Promises = () => {
  const { isManager } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState(null);

  // Date range filter (replaced single selectedDate)
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

  // Agent filter
  const [selectedAgent, setSelectedAgent] = useState('');
  const [agents, setAgents] = useState([]);

  const [sortBy, setSortBy] = useState('totalCalls');
  const [sortOrder, setSortOrder] = useState('desc');

  // Drill-down modal state
  const [drillDownModal, setDrillDownModal] = useState({
    open: false,
    type: null,
    title: '',
    data: [],
    loading: false
  });

  // Fetch statistics
  const fetchStatistics = async () => {
    setLoading(true);
    try {
      // Build query params
      const params = new URLSearchParams({
        startDate,
        endDate
      });

      // Add agent filter if selected
      if (selectedAgent) {
        params.append('agentId', selectedAgent);
      }

      const response = await apiService.get(
        `${process.env.REACT_APP_API_HOST_COLLECTIONS}/promises-statistics?${params.toString()}`
      );
      const data = await response.json();
      setStatistics(data);
    } catch (error) {
      console.error('Error fetching promises statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch collections officers for filter (managers only)
  const fetchAgents = async () => {
    try {
      const response = await apiService.get(
        `${process.env.REACT_APP_API_HOST_COLLECTIONS}/collections-officers`
      );
      const data = await response.json();
      setAgents(data.collectionsOfficers || []);
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  // Fetch statistics on initial load only
  useEffect(() => {
    fetchStatistics();
  }, []); // Empty dependency array - only runs on mount

  useEffect(() => {
    if (statistics?.isManager) {
      fetchAgents();
    }
  }, [statistics?.isManager]);

  // Format currency
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      const d = new Date(date);
      return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (e) {
      return 'N/A';
    }
  };

  // Format time
  const formatTime = (date) => {
    if (!date) return '';
    try {
      const d = new Date(date);
      return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } catch (e) {
      return '';
    }
  };

  // Drill-down functions
  const fetchDrillDownData = async (metricType) => {
    setDrillDownModal({
      open: true,
      type: metricType,
      title: getMetricTitle(metricType),
      data: [],
      loading: true
    });

    try {
      const params = new URLSearchParams({ startDate, endDate });
      if (selectedAgent) params.append('agentId', selectedAgent);

      const endpoint = `${process.env.REACT_APP_API_HOST_COLLECTIONS}/drill-down/${metricType}`;
      const response = await apiService.get(`${endpoint}?${params.toString()}`);
      const data = await response.json();

      setDrillDownModal(prev => ({
        ...prev,
        data: data.items || [],
        loading: false
      }));
    } catch (error) {
      console.error('Error fetching drill-down data:', error);
      setDrillDownModal(prev => ({ ...prev, loading: false }));
    }
  };

  const getMetricTitle = (type) => {
    const titles = {
      'total-calls': 'Total Calls',
      'clients-reached': 'Clients Reached',
      'promises-to-pay': 'Promises to Pay',
      'payments-collected': 'Payments Collected'
    };
    return titles[type] || 'Details';
  };

  const closeDrillDownModal = () => {
    setDrillDownModal({
      open: false,
      type: null,
      title: '',
      data: [],
      loading: false
    });
  };

  // Get resolution type label and color
  const getResolutionTypeInfo = (type) => {
    const types = {
      client_picked_up: { label: 'Client Picked Up', color: 'bg-green-100 text-green-800' },
      client_no_answer: { label: 'No Answer', color: 'bg-yellow-100 text-yellow-800' },
      promise_to_pay: { label: 'Promise to Pay', color: 'bg-blue-100 text-blue-800' },
      payment_collected: { label: 'Payment Collected', color: 'bg-green-100 text-green-900' },
      resolved: { label: 'Resolved', color: 'bg-purple-100 text-purple-800' },
      wrong_number: { label: 'Wrong Number', color: 'bg-red-100 text-red-800' },
      dispute: { label: 'Dispute', color: 'bg-orange-100 text-orange-800' },
      other: { label: 'Other', color: 'bg-gray-100 text-gray-800' }
    };
    return types[type] || { label: type, color: 'bg-gray-100 text-gray-800' };
  };

  // Sort agent stats
  const sortAgentStats = (stats) => {
    if (!stats || stats.length === 0) return [];

    const sorted = [...stats].sort((a, b) => {
      const aVal = a[sortBy] || 0;
      const bVal = b[sortBy] || 0;

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    return sorted;
  };

  // Handle sort click
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  if (!statistics) {
    return (
      <div className="p-6">
        <div className="text-center text-gray-600">
          <p>No data available</p>
        </div>
      </div>
    );
  }

  const { summary, resolutionBreakdown, agentStats, recentCalls } = statistics;

  // Chart data for resolution breakdown
  const chartData = {
    labels: resolutionBreakdown.map(r => getResolutionTypeInfo(r.type).label),
    datasets: [{
      data: resolutionBreakdown.map(r => r.count),
      backgroundColor: [
        'rgba(34, 197, 94, 0.8)',  // green - client_picked_up
        'rgba(251, 191, 36, 0.8)',  // yellow - client_no_answer
        'rgba(59, 130, 246, 0.8)',  // blue - promise_to_pay
        'rgba(22, 163, 74, 0.8)',   // dark green - payment_collected
        'rgba(147, 51, 234, 0.8)',  // purple - resolved
        'rgba(239, 68, 68, 0.8)',   // red - wrong_number
        'rgba(249, 115, 22, 0.8)',  // orange - dispute
        'rgba(156, 163, 175, 0.8)'  // gray - other
      ],
      borderWidth: 2,
      borderColor: '#fff'
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 15,
          font: {
            size: 12
          }
        }
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.label || '';
            const value = context.parsed || 0;
            const percentage = resolutionBreakdown[context.dataIndex]?.percentage || 0;
            return `${label}: ${value} (${percentage}%)`;
          }
        }
      }
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Promises & Resolutions</h1>
          <p className="text-gray-600 mt-1">Track collection activity and performance</p>
        </div>

        {/* Filters Section */}
        <div className="mb-8 bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Filters</h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Date Range Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                max={endDate || new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate}
                max={new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
              />
            </div>

            {/* Agent Filter (Managers only) */}
            {statistics?.isManager && (
              <div>
                <SearchableSelect
                  options={agents}
                  value={selectedAgent}
                  onChange={setSelectedAgent}
                  placeholder="All Agents"
                  label="Filter by Agent"
                  getOptionLabel={(agent) => `${agent.firstName} ${agent.lastName}`}
                  getOptionValue={(agent) => agent._id}
                />
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="mt-4 flex justify-end gap-3">
            <button
              onClick={() => {
                setStartDate(new Date().toISOString().split('T')[0]);
                setEndDate(new Date().toISOString().split('T')[0]);
                setSelectedAgent('');
                // Apply filters after clearing (fetch with default values)
                setTimeout(() => fetchStatistics(), 0);
              }}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Clear Filters
            </button>
            <button
              onClick={() => fetchStatistics()}
              disabled={loading}
              className="px-6 py-2 text-sm font-medium text-white bg-cyan-600 rounded-lg hover:bg-cyan-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Loading...' : 'Apply Filters'}
            </button>
          </div>
        </div>

        {/* Date Range Display */}
        {statistics && (
          <div className="mb-6">
            <p className="text-gray-600">
              {startDate === endDate ? (
                `Statistics for ${new Date(startDate).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}`
              ) : (
                `Statistics from ${new Date(startDate).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                })} to ${new Date(endDate).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                })}`
              )}
              {selectedAgent && statistics?.isManager && (
                <span className="ml-2 px-2 py-1 text-xs bg-cyan-100 text-cyan-800 rounded">
                  {agents.find(a => a._id === selectedAgent)?.firstName} {agents.find(a => a._id === selectedAgent)?.lastName}
                </span>
              )}
            </p>
          </div>
        )}

        {/* Metric Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Total Calls */}
          <div
            onClick={() => fetchDrillDownData('total-calls')}
            className="bg-white rounded-lg shadow-sm p-6 border border-gray-200 cursor-pointer hover:shadow-md hover:border-cyan-300 transition-all"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">Total Calls</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{summary.totalCalls}</p>
                <p className="text-sm text-cyan-600 mt-1">
                  <Phone className="inline w-4 h-4 mr-1" />
                  Contact attempts • Click to view details
                </p>
              </div>
              <div className="bg-cyan-100 p-3 rounded-full">
                <Phone className="text-cyan-600 w-6 h-6" />
              </div>
            </div>
          </div>

          {/* Clients Reached */}
          <div
            onClick={() => fetchDrillDownData('clients-reached')}
            className="bg-white rounded-lg shadow-sm p-6 border border-gray-200 cursor-pointer hover:shadow-md hover:border-green-300 transition-all"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">Clients Reached</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{summary.clientsReached}</p>
                <p className="text-sm text-green-600 mt-1">
                  <TrendingUp className="inline w-4 h-4 mr-1" />
                  {summary.reachRate}% reach rate • Click to view details
                </p>
              </div>
              <div className="bg-green-100 p-3 rounded-full">
                <CheckCircle className="text-green-600 w-6 h-6" />
              </div>
            </div>
          </div>

          {/* Promises */}
          <div
            onClick={() => fetchDrillDownData('promises-to-pay')}
            className="bg-white rounded-lg shadow-sm p-6 border border-gray-200 cursor-pointer hover:shadow-md hover:border-blue-300 transition-all"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">Promises to Pay</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{summary.promisesCount}</p>
                <p className="text-sm text-blue-600 mt-1">
                  <Schedule className="inline w-4 h-4 mr-1" />
                  {formatCurrency(summary.promisesAmount)} • Click to view details
                </p>
              </div>
              <div className="bg-blue-100 p-3 rounded-full">
                <Schedule className="text-blue-600 w-6 h-6" />
              </div>
            </div>
          </div>

          {/* Payments */}
          <div
            onClick={() => fetchDrillDownData('payments-collected')}
            className="bg-white rounded-lg shadow-sm p-6 border border-gray-200 cursor-pointer hover:shadow-md hover:border-green-300 transition-all"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">Payments Collected</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{summary.paymentsCollected}</p>
                <p className="text-sm text-green-600 mt-1">
                  <Payment className="inline w-4 h-4 mr-1" />
                  {formatCurrency(summary.paymentsAmount)} • Click to view details
                </p>
              </div>
              <div className="bg-green-100 p-3 rounded-full">
                <Payment className="text-green-600 w-6 h-6" />
              </div>
            </div>
          </div>
        </div>

        {/* Performance Analytics Widgets */}
        {statistics.analytics && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {/* PTP Conversion Rate */}
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
              <p className="text-sm text-gray-600 font-medium">PTP Conversion Rate</p>
              <p className={`text-3xl font-bold mt-2 ${
                statistics.analytics.ptpConversionRate > 50 ? 'text-green-600' :
                statistics.analytics.ptpConversionRate >= 25 ? 'text-yellow-600' : 'text-red-600'
              }`}>
                {statistics.analytics.ptpConversionRate}%
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {statistics.analytics.ptpFulfilled} fulfilled of {statistics.analytics.ptpTotal} resolved
              </p>
            </div>

            {/* Collection Rate */}
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
              <p className="text-sm text-gray-600 font-medium mb-2">Collection Rate</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.analytics.collectionRate.overall}%</p>
              <div className="mt-2 space-y-1 text-xs text-gray-600">
                <div className="flex justify-between"><span>Principal</span><span className="font-medium">{statistics.analytics.collectionRate.principal}%</span></div>
                <div className="flex justify-between"><span>Interest</span><span className="font-medium">{statistics.analytics.collectionRate.interest}%</span></div>
                <div className="flex justify-between"><span>Fee</span><span className="font-medium">{statistics.analytics.collectionRate.fee}%</span></div>
                <div className="flex justify-between"><span>Penalty</span><span className="font-medium">{statistics.analytics.collectionRate.penalty}%</span></div>
              </div>
            </div>

            {/* Calls per Collector per Day */}
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
              <p className="text-sm text-gray-600 font-medium">Calls / Collector / Day</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{statistics.analytics.callsPerCollectorPerDay}</p>
              <p className="text-sm text-gray-500 mt-1">
                {statistics.analytics.activeAgents} agents over {statistics.analytics.daysWithCalls} days
              </p>
            </div>

            {/* Promise Broken Rate */}
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
              <p className="text-sm text-gray-600 font-medium">Promise Broken Rate</p>
              <p className={`text-3xl font-bold mt-2 ${
                statistics.analytics.promiseBrokenRate < 25 ? 'text-green-600' :
                statistics.analytics.promiseBrokenRate <= 50 ? 'text-yellow-600' : 'text-red-600'
              }`}>
                {statistics.analytics.promiseBrokenRate}%
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {statistics.analytics.brokenCount} broken of {statistics.analytics.resolvedPromises} resolved
              </p>
            </div>
          </div>
        )}

        {/* Resolution Breakdown Chart */}
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Resolution Breakdown</h2>
          <div className="h-80">
            <Doughnut data={chartData} options={chartOptions} />
          </div>
        </div>

        {/* Agent Stats Section (Manager Only) */}
        {isManager && agentStats.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Agent Performance</h2>

            {/* Agent Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {sortAgentStats(agentStats).slice(0, 6).map((agent) => (
                <div key={agent.agentId} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-900">{agent.agentName}</h3>
                    <span className="text-sm text-gray-600">{agent.reachRate}% reach</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-600">Calls:</span>
                      <span className="font-semibold text-gray-900 ml-1">{agent.totalCalls}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Reached:</span>
                      <span className="font-semibold text-gray-900 ml-1">{agent.clientsReached}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Promises:</span>
                      <span className="font-semibold text-gray-900 ml-1">{agent.promisesCount}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Collected:</span>
                      <span className="font-semibold text-gray-900 ml-1">{agent.paymentsCollected}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Agent Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      onClick={() => handleSort('agentName')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      Agent {sortBy === 'agentName' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                    <th
                      onClick={() => handleSort('totalCalls')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      Total Calls {sortBy === 'totalCalls' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                    <th
                      onClick={() => handleSort('clientsReached')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      Reached {sortBy === 'clientsReached' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                    <th
                      onClick={() => handleSort('reachRate')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      Reach % {sortBy === 'reachRate' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                    <th
                      onClick={() => handleSort('promisesCount')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      Promises {sortBy === 'promisesCount' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                    <th
                      onClick={() => handleSort('promisesAmount')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      Promise Amount {sortBy === 'promisesAmount' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                    <th
                      onClick={() => handleSort('paymentsCollected')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      Collected {sortBy === 'paymentsCollected' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortAgentStats(agentStats).map((agent) => (
                    <tr key={agent.agentId} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {agent.agentName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {agent.totalCalls}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {agent.clientsReached}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {agent.reachRate}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {agent.promisesCount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatCurrency(agent.promisesAmount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {agent.paymentsCollected} ({formatCurrency(agent.paymentsAmount)})
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Recent Calls List */}
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Activity</h2>

          {recentCalls.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No activity recorded for this date</p>
            </div>
          ) : (
            <div className="space-y-4">
              {recentCalls.map((call) => {
                const resolutionInfo = getResolutionTypeInfo(call.resolutionType);
                return (
                  <div key={call._id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <button
                            onClick={() => navigate(`/loan/${call.loanId}`)}
                            className="text-cyan-600 hover:text-cyan-700 font-semibold"
                          >
                            Loan #{call.loanNumber}
                          </button>
                          <span className="text-gray-600">•</span>
                          <span className="text-gray-900">{call.clientName}</span>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${resolutionInfo.color}`}>
                            {resolutionInfo.label}
                          </span>
                        </div>

                        {call.resolutionType === 'promise_to_pay' && call.promiseAmount && (
                          <div className="mb-2 text-sm">
                            <span className="text-blue-600 font-semibold">
                              Promise: {formatCurrency(call.promiseAmount)}
                            </span>
                            {call.promiseDate && (
                              <span className="text-gray-600 ml-2">
                                by {formatDate(call.promiseDate)}
                              </span>
                            )}
                          </div>
                        )}

                        {call.resolutionType === 'payment_collected' && call.promiseAmount && (
                          <div className="mb-2 text-sm">
                            <span className="text-green-700 font-semibold">
                              Amount: {formatCurrency(call.promiseAmount)}
                            </span>
                          </div>
                        )}

                        {call.notes && (
                          <p className="text-sm text-gray-600 mt-2">{call.notes}</p>
                        )}
                      </div>

                      <div className="text-right text-sm text-gray-500 ml-4">
                        <div>{call.agentName}</div>
                        <div>{formatDate(call.createdAt)}</div>
                        <div>{formatTime(call.createdAt)}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Drill-Down Modal */}
        {drillDownModal.open && (
          <DrillDownModal
            isOpen={drillDownModal.open}
            onClose={closeDrillDownModal}
            title={drillDownModal.title}
            metricType={drillDownModal.type}
            data={drillDownModal.data}
            loading={drillDownModal.loading}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
            formatTime={formatTime}
            navigate={navigate}
          />
        )}
      </div>
    </div>
  );
};

export default Promises;
